import { useMemo, useState } from 'react';
import { AlertTriangle, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { useRevokeMember } from '@/hooks/use-committee';

interface RevokeTarget {
  memberId: string;
  name: string;
  email: string;
}

interface RevokeEvaluatorDialogProps {
  open: boolean;
  eventId: string;
  target: RevokeTarget | null;
  onOpenChange: (open: boolean) => void;
}

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'EV';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

export function RevokeEvaluatorDialog({
  open,
  eventId,
  target,
  onOpenChange,
}: RevokeEvaluatorDialogProps) {
  const [confirmed, setConfirmed] = useState(false);
  const revokeMember = useRevokeMember(eventId);

  const displayName = useMemo(() => target?.name || target?.email || '', [target]);
  const canSubmit = Boolean(target?.memberId) && confirmed && !revokeMember.isPending;

  const handleClose = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setConfirmed(false);
    }
  };

  const handleSubmit = async () => {
    if (!target?.memberId || !confirmed) return;

    try {
      await revokeMember.mutateAsync({ memberId: target.memberId, confirmation: true });
      toast.success('Evaluador revocado correctamente.');
      handleClose(false);
    } catch {
      toast.error('No fue posible revocar el evaluador. Intente de nuevo.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-[#1e3a5f]">
            <span className="flex items-center gap-3">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-red-100 text-destructive">
                <ShieldAlert className="h-5 w-5" />
              </span>
              Revocar Evaluador
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-lg bg-muted p-4">
            <div className="flex items-center gap-3">
              <Avatar className="h-12 w-12">
                <AvatarFallback>{initialsFromName(displayName)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-gray-900">{displayName}</p>
                <p className="text-sm text-gray-600">{target?.email || '-'}</p>
              </div>
            </div>
          </div>

          <Alert className="border-red-200 bg-red-50 text-red-900">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            <AlertDescription>
              <p>El evaluador perderá acceso a este evento.</p>
              <p>Las evaluaciones ya completadas se conservarán en el historial.</p>
            </AlertDescription>
          </Alert>

          <label className="flex items-start gap-3 text-base text-gray-900">
            <Checkbox checked={confirmed} onCheckedChange={(checked) => setConfirmed(Boolean(checked))} />
            <span>Confirmo que deseo revocar el acceso de este evaluador.</span>
          </label>
        </div>

        <DialogFooter className="!flex !w-full !flex-row items-center justify-between gap-2">
          <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={revokeMember.isPending}>
            Cancelar
          </Button>
          <Button
            type="button"
            className="min-w-[150px] border border-red-700 bg-red-600 text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300 disabled:text-white"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {revokeMember.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Revocar acceso
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
